import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  MenuItem,
} from "@mui/material";

import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  Timestamp,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  deleteDoc,
  setDoc,
  orderBy,
} from "firebase/firestore";

export default function SeatingDetails() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
  });

  const [propertyId, setPropertyId] = useState("");
  const [categories, setCategories] = useState([]);
  const [seatings, setSeatings] = useState([]);
  const [search, setSearch] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const [seatcatid, setSeatcatid] = useState("");
  const [seatname, setSeatname] = useState("");

  const [editId, setEditId] = useState("");
  const [editCat, setEditCat] = useState("");
  const [editSeat, setEditSeat] = useState("");

  /* ================= PROPERTY ID ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      const snap = await getDoc(doc(db, "authroles", user.uid));
      if (!snap.exists()) {
        Toast.fire({ icon: "error", title: "Property not assigned" });
        return;
      }
      setPropertyId(snap.data().propertyId);
    });

    return () => unsub();
  }, []);

  /* ================= FETCH CATEGORIES ================= */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "seatingcats"),
      where("propertyId", "==", propertyId)
    );

    return onSnapshot(q, (snap) => {
      setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [propertyId]);

  /* ================= FETCH SEATINGS ================= */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "seatings"),
      where("propertyId", "==", propertyId),
      orderBy('createdAt','asc')
    );

    return onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => {
        const cat = categories.find((c) => c.id === d.data().seatcatid);
        return {
          id: d.id,
          seatname: d.data().seatname,
          catname: cat?.catname || "",
          seatcatid: d.data().seatcatid,
        };
      });
      setSeatings(data);
    });
  }, [propertyId, categories]);

  /* ================= ADD ================= */
  const handleAdd = async () => {
    if (!seatname || !seatcatid) {
      Toast.fire({ icon: "error", title: "Fill all fields" });
      return;
    }

    await addDoc(collection(db, "seatings"), {
      propertyId,
      seatcatid,
      seatname,
      createdAt: Timestamp.now(),
    });

    setSeatname("");
    setSeatcatid("");
    setOpenAdd(false);

    Toast.fire({ icon: "success", title: "Seating added" });
  };

  /* ================= EDIT ================= */
  const openEditModal = (row) => {
    setEditId(row.id);
    setEditSeat(row.seatname);
    setEditCat(row.seatcatid);
    setOpenEdit(true);
  };

  const handleUpdate = async () => {
    await setDoc(
      doc(db, "seatings", editId),
      {
        seatcatid: editCat,
        seatname: editSeat,
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );

    setOpenEdit(false);
    Toast.fire({ icon: "success", title: "Updated successfully" });
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "seatings", id));
    Toast.fire({ icon: "success", title: "Deleted successfully" });
  };

  /* ================= FILTER ================= */
  const filtered = seatings.filter((s) =>
    s.seatname.toLowerCase().includes(search.toLowerCase())
  );

  /* ================= UI ================= */
  return (
    <Box className=" p-3">
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Seating Details</Typography>
            <Button variant="contained" onClick={() => setOpenAdd(true)}>
              Add Seating
            </Button>
          </Stack>

          <TextField
            fullWidth
            size="small"
            placeholder="Search seating..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Seating Name</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row, i) => (
                  <TableRow key={row.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{row.catname}</TableCell>
                    <TableCell>{row.seatname}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openEditModal(row)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDelete(row.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ADD */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth>
        <DialogTitle>Add Seating</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Seating Category"
            value={seatcatid}
            onChange={(e) => setSeatcatid(e.target.value)}
            sx={{ mt: 2 }}
          >
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.catname}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Seating Name"
            value={seatname}
            onChange={(e) => setSeatname(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth>
        <DialogTitle>Edit Seating</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Seating Category"
            value={editCat}
            onChange={(e) => setEditCat(e.target.value)}
            sx={{ mt: 2 }}
          >
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.catname}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Seating Name"
            value={editSeat}
            onChange={(e) => setEditSeat(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
